import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { collection, doc, onSnapshot, getCountFromServer } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { RootState } from '../index';
import { getSystemStatus, getAlgoliaUsage } from '@/lib/api';

interface SystemStatus {
  algolia: {
    status: 'enabled' | 'disabled';
    lastStatusChange?: number; // timestamp as number
    lastFullSyncTime?: number;
    lastUpdated?: number;
  };
  aggregation: {
    status: 'enabled' | 'disabled';
    lastStatusChange?: number;
    lastFullSyncTime?: number;
    lastUpdated?: number;
  };
}

interface AlgoliaQueueStatus {
  queueSize: number;
  failedQueueSize: number;
  oldestQueuedItem?: number; // timestamp as number
  newestQueuedItem?: number; // timestamp as number
}

interface AlgoliaUsageStats {
  totalRecords: number;
  operations: {
    searches: number;
    records: {
      added: number;
      updated: number;
      deleted: number;
    };
  };
  indices: {
    drinks: {
      objectCount: number;
      size: number;
    };
    locations: {
      objectCount: number;
      size: number;
    };
  };
  updatedAt?: number; // timestamp as number
}

interface CollectionStats {
  locations: number;
  drinks: number;
  brands: number;
  sightings: number;
  reviews: number;
  users: number;
  confirmations: number;
  flavors: number;
  attributes: number;
  visitedLocations: number;
  algoliaUpdateQueue: number;
  algoliaFailedQueue: number;
}

interface AdminState {
  systemStatus: {
    data: SystemStatus | null;
    loading: boolean;
    error: string | null;
  };
  queueStatus: {
    data: AlgoliaQueueStatus | null;
    loading: boolean;
    error: string | null;
  };
  algoliaUsage: {
    data: AlgoliaUsageStats | null;
    loading: boolean;
    error: string | null;
  };
  collectionStats: {
    data: CollectionStats | null;
    loading: boolean;
    error: string | null;
  };
  listenersActive: boolean;
  unsubscribeFunctions: (() => void)[] | null;
}

const initialState: AdminState = {
  systemStatus: {
    data: null,
    loading: false,
    error: null
  },
  queueStatus: {
    data: null,
    loading: false,
    error: null
  },
  algoliaUsage: {
    data: null,
    loading: false,
    error: null
  },
  collectionStats: {
    data: null,
    loading: false,
    error: null
  },
  listenersActive: false,
  unsubscribeFunctions: null
};

// Thunk to fetch collection stats
export const fetchCollectionStats = createAsyncThunk(
  'admin/fetchCollectionStats',
  async (_, { dispatch }) => {
    dispatch(setCollectionStatsLoading(true));
    try {
      const collections = [
        'locations', 'drinks', 'brands', 'sightings', 
        'reviews', 'users', 'flavors', 'attributes'
      ];
      
      const stats: Partial<CollectionStats> = {};
      
      // Fetch counts for main collections
      await Promise.all(collections.map(async (collName) => {
        const snap = await getCountFromServer(collection(db, collName));
        stats[collName as keyof CollectionStats] = snap.data().count;
      }));
      
      // Fetch queue collections
      const algoliaUpdateQueueSnap = await getCountFromServer(collection(db, 'algoliaUpdateQueue'));
      stats.algoliaUpdateQueue = algoliaUpdateQueueSnap.data().count;
      
      const algoliaFailedQueueSnap = await getCountFromServer(collection(db, 'algoliaFailedQueue'));
      stats.algoliaFailedQueue = algoliaFailedQueueSnap.data().count;
      
      // Estimate confirmations count (this is a subcollection, harder to count)
      // This is an estimate as we'd need to query across all sightings
      // For now, we set to -1 to indicate it's not counted precisely
      stats.confirmations = -1;
      stats.visitedLocations = -1;
      
      dispatch(updateCollectionStats(stats as CollectionStats));
      return stats as CollectionStats;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch(setCollectionStatsError(errorMessage));
      throw error;
    } finally {
      dispatch(setCollectionStatsLoading(false));
    }
  }
);

// Thunk to fetch Algolia usage stats
export const fetchAlgoliaUsage = createAsyncThunk(
  'admin/fetchAlgoliaUsage',
  async (_, { dispatch }) => {
    dispatch(setAlgoliaUsageLoading(true));
    try {
      const response = await getAlgoliaUsage();
      if (response && response.success && response.usage) {
        const formattedUsage: AlgoliaUsageStats = {
          totalRecords: response.usage.records?.total || 0,
          operations: {
            searches: response.usage.operations_last_30_days?.searches || 0,
            records: {
              added: response.usage.operations_last_30_days?.indexing || 0,
              updated: 0, // Not provided directly by API
              deleted: 0  // Not provided directly by API
            }
          },
          indices: {
            drinks: {
              objectCount: response.usage.records?.drinks || 0,
              size: 0 // Not provided directly by API
            },
            locations: {
              objectCount: response.usage.records?.locations || 0,
              size: 0 // Not provided directly by API
            }
          },
          updatedAt: response.usage.sync?.lastFullSync ? new Date(response.usage.sync.lastFullSync).getTime() : undefined
        };
        dispatch(updateAlgoliaUsage(formattedUsage));
        return formattedUsage;
      }
      throw new Error('No Algolia usage data returned');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch(setAlgoliaUsageError(errorMessage));
      throw error;
    } finally {
      dispatch(setAlgoliaUsageLoading(false));
    }
  }
);

// Thunk to fetch system status
export const fetchSystemStatus = createAsyncThunk(
  'admin/fetchSystemStatus',
  async (_, { dispatch }) => {
    dispatch(setSystemStatusLoading(true));
    try {
      const status = await getSystemStatus();
      if (status) {
        const systemStatus: SystemStatus = {
          algolia: {
            status: status.algolia?.status || 'disabled',
            lastStatusChange: status.algolia?.lastStatusChange ? status.algolia.lastStatusChange.toMillis() : undefined,
            lastFullSyncTime: status.algolia?.lastFullSyncTime ? status.algolia.lastFullSyncTime.toMillis() : undefined,
            lastUpdated: status.algolia?.lastUpdated ? status.algolia.lastUpdated.toMillis() : undefined
          },
          aggregation: {
            status: status.aggregation?.status || 'disabled',
            lastStatusChange: status.aggregation?.lastStatusChange ? status.aggregation.lastStatusChange.toMillis() : undefined,
            lastFullSyncTime: status.aggregation?.lastFullRecalculationTime ? status.aggregation.lastFullRecalculationTime.toMillis() : undefined,
            lastUpdated: status.aggregation?.lastUpdated ? status.aggregation.lastUpdated.toMillis() : undefined
          }
        };
        dispatch(updateSystemStatus(systemStatus));
        return systemStatus;
      }
      throw new Error('No system status data returned');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch(setSystemStatusError(errorMessage));
      throw error;
    } finally {
      dispatch(setSystemStatusLoading(false));
    }
  }
);

// Async thunk to set up all admin listeners
export const initializeAdminListeners = createAsyncThunk(
  'admin/initializeListeners',
  async (_, { dispatch }) => {
    const unsubscribes: (() => void)[] = [];

    // System settings listeners
    try {
      // Get initial data directly first
      // await dispatch(fetchSystemStatus()); // Removed this line
      await dispatch(fetchAlgoliaUsage());
      await dispatch(fetchCollectionStats());

      // Setup Algolia status listener
      const algoliaStatusUnsubscribe = onSnapshot(
        doc(db, 'systemSettings', 'algolia'),
        (snapshot) => {
          const data = snapshot.data();
          if (data) {
            dispatch(updateAlgoliaSystemStatus({
              status: data.status,
              lastStatusChange: data.lastStatusChange?.toMillis(),
              lastFullSyncTime: data.lastFullSyncTime?.toMillis(),
              lastUpdated: data.lastUpdated?.toMillis()
            }));
          }
        },
        (error) => {
          dispatch(setSystemStatusError(error.message));
        }
      );
      unsubscribes.push(algoliaStatusUnsubscribe);
      
      // Setup Aggregation status listener
      const aggregationStatusUnsubscribe = onSnapshot(
        doc(db, 'systemSettings', 'aggregation'),
        (snapshot) => {
          const data = snapshot.data();
          if (data) {
            dispatch(updateAggregationSystemStatus({
              status: data.status,
              lastStatusChange: data.lastStatusChange?.toMillis(),
              lastFullSyncTime: data.lastFullRecalculationTime?.toMillis(),
              lastUpdated: data.lastUpdated?.toMillis()
            }));
          }
        },
        (error) => {
          dispatch(setSystemStatusError(error.message));
        }
      );
      unsubscribes.push(aggregationStatusUnsubscribe);
      
      // Setup Queue size listener
      const queueStatusUnsubscribe = onSnapshot(
        collection(db, 'algoliaUpdateQueue'),
        (snapshot) => {
          let oldestTimestamp = Number.MAX_SAFE_INTEGER;
          let newestTimestamp = 0;
          
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.queuedAt) {
              const timestamp = data.queuedAt.toMillis();
              if (timestamp < oldestTimestamp) oldestTimestamp = timestamp;
              if (timestamp > newestTimestamp) newestTimestamp = timestamp;
            }
          });
          
          dispatch(updateQueueStatus({
            queueSize: snapshot.size,
            oldestQueuedItem: oldestTimestamp !== Number.MAX_SAFE_INTEGER ? oldestTimestamp : undefined,
            newestQueuedItem: newestTimestamp > 0 ? newestTimestamp : undefined
          }));
        },
        (error) => {
          dispatch(setQueueStatusError(error.message));
        }
      );
      unsubscribes.push(queueStatusUnsubscribe);
      
      // Setup Failed queue size listener
      const failedQueueStatusUnsubscribe = onSnapshot(
        collection(db, 'algoliaFailedQueue'),
        (snapshot) => {
          dispatch(updateFailedQueueSize(snapshot.size));
        },
        (error) => {
          dispatch(setQueueStatusError(error.message));
        }
      );
      unsubscribes.push(failedQueueStatusUnsubscribe);
      
      // Set up periodic refresh for Algolia usage and collection stats
      const statsRefreshInterval = setInterval(() => {
        dispatch(fetchAlgoliaUsage());
        dispatch(fetchCollectionStats());
      }, 60000); // Refresh every minute
      
      // Create a function to clear the interval
      const clearIntervalFn = () => clearInterval(statsRefreshInterval);
      unsubscribes.push(clearIntervalFn);
      
      return unsubscribes;
    } catch (error) {
      // Clean up any listeners that were set up before error
      unsubscribes.forEach(unsub => unsub());
      throw error;
    }
  }
);

// Thunk to clean up all admin listeners
export const cleanupAdminListeners = createAsyncThunk(
  'admin/cleanupListeners',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const unsubscribes = state.admin.unsubscribeFunctions;
    
    if (unsubscribes) {
      unsubscribes.forEach(unsub => unsub());
    }
    
    return true;
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    updateSystemStatus(state, action: PayloadAction<SystemStatus>) {
      state.systemStatus.data = action.payload;
    },
    updateAlgoliaSystemStatus(state, action: PayloadAction<Partial<SystemStatus['algolia']>>) {
      if (!state.systemStatus.data) {
        state.systemStatus.data = {
          algolia: action.payload as SystemStatus['algolia'],
          aggregation: {
            status: 'disabled'
          }
        };
      } else {
        state.systemStatus.data.algolia = {
          ...state.systemStatus.data.algolia,
          ...action.payload
        };
      }
    },
    updateAggregationSystemStatus(state, action: PayloadAction<Partial<SystemStatus['aggregation']>>) {
      if (!state.systemStatus.data) {
        state.systemStatus.data = {
          algolia: {
            status: 'disabled'
          },
          aggregation: action.payload as SystemStatus['aggregation']
        };
      } else {
        state.systemStatus.data.aggregation = {
          ...state.systemStatus.data.aggregation,
          ...action.payload
        };
      }
    },
    setSystemStatusLoading(state, action: PayloadAction<boolean>) {
      state.systemStatus.loading = action.payload;
    },
    setSystemStatusError(state, action: PayloadAction<string | null>) {
      state.systemStatus.error = action.payload;
    },
    updateQueueStatus(state, action: PayloadAction<Partial<AlgoliaQueueStatus>>) {
      if (!state.queueStatus.data) {
        state.queueStatus.data = action.payload as AlgoliaQueueStatus;
      } else {
        state.queueStatus.data = {
          ...state.queueStatus.data,
          ...action.payload
        };
      }
    },
    updateFailedQueueSize(state, action: PayloadAction<number>) {
      if (!state.queueStatus.data) {
        state.queueStatus.data = {
          queueSize: 0,
          failedQueueSize: action.payload
        };
      } else {
        state.queueStatus.data.failedQueueSize = action.payload;
      }
    },
    setQueueStatusLoading(state, action: PayloadAction<boolean>) {
      state.queueStatus.loading = action.payload;
    },
    setQueueStatusError(state, action: PayloadAction<string | null>) {
      state.queueStatus.error = action.payload;
    },
    updateAlgoliaUsage(state, action: PayloadAction<AlgoliaUsageStats>) {
      state.algoliaUsage.data = action.payload;
    },
    setAlgoliaUsageLoading(state, action: PayloadAction<boolean>) {
      state.algoliaUsage.loading = action.payload;
    },
    setAlgoliaUsageError(state, action: PayloadAction<string | null>) {
      state.algoliaUsage.error = action.payload;
    },
    updateCollectionStats(state, action: PayloadAction<CollectionStats>) {
      state.collectionStats.data = action.payload;
    },
    setCollectionStatsLoading(state, action: PayloadAction<boolean>) {
      state.collectionStats.loading = action.payload;
    },
    setCollectionStatsError(state, action: PayloadAction<string | null>) {
      state.collectionStats.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAdminListeners.pending, (state) => {
        state.systemStatus.loading = true;
        state.queueStatus.loading = true;
        state.algoliaUsage.loading = true;
        state.collectionStats.loading = true;
      })
      .addCase(initializeAdminListeners.fulfilled, (state, action) => {
        state.systemStatus.loading = false;
        state.queueStatus.loading = false;
        state.algoliaUsage.loading = false;
        state.collectionStats.loading = false;
        state.listenersActive = true;
        state.unsubscribeFunctions = action.payload;
      })
      .addCase(initializeAdminListeners.rejected, (state, action) => {
        state.systemStatus.loading = false;
        state.queueStatus.loading = false;
        state.algoliaUsage.loading = false;
        state.collectionStats.loading = false;
        
        const errorMessage = action.error.message || 'Failed to initialize admin listeners';
        state.systemStatus.error = errorMessage;
        state.queueStatus.error = errorMessage;
        state.algoliaUsage.error = errorMessage;
        state.collectionStats.error = errorMessage;
      })
      .addCase(cleanupAdminListeners.fulfilled, (state) => {
        state.listenersActive = false;
        state.unsubscribeFunctions = null;
      });
  }
});

export const {
  updateSystemStatus,
  updateAlgoliaSystemStatus,
  updateAggregationSystemStatus,
  setSystemStatusLoading,
  setSystemStatusError,
  updateQueueStatus,
  updateFailedQueueSize,
  setQueueStatusLoading,
  setQueueStatusError,
  updateAlgoliaUsage,
  setAlgoliaUsageLoading,
  setAlgoliaUsageError,
  updateCollectionStats,
  setCollectionStatsLoading,
  setCollectionStatsError
} = adminSlice.actions;

// Selectors
export const selectSystemStatus = (state: RootState) => state.admin.systemStatus.data;
export const selectSystemStatusLoading = (state: RootState) => state.admin.systemStatus.loading;
export const selectSystemStatusError = (state: RootState) => state.admin.systemStatus.error;

export const selectQueueStatus = (state: RootState) => state.admin.queueStatus.data;
export const selectQueueStatusLoading = (state: RootState) => state.admin.queueStatus.loading;
export const selectQueueStatusError = (state: RootState) => state.admin.queueStatus.error;

export const selectAlgoliaUsage = (state: RootState) => state.admin.algoliaUsage.data;
export const selectAlgoliaUsageLoading = (state: RootState) => state.admin.algoliaUsage.loading;
export const selectAlgoliaUsageError = (state: RootState) => state.admin.algoliaUsage.error;

export const selectCollectionStats = (state: RootState) => state.admin.collectionStats.data;
export const selectCollectionStatsLoading = (state: RootState) => state.admin.collectionStats.loading;
export const selectCollectionStatsError = (state: RootState) => state.admin.collectionStats.error;

export const selectIsAlgoliaEnabled = (state: RootState) => 
  state.admin.systemStatus.data?.algolia.status === 'enabled';
export const selectIsAggregationEnabled = (state: RootState) => 
  state.admin.systemStatus.data?.aggregation.status === 'enabled';

export const selectAdminListenersActive = (state: RootState) => state.admin.listenersActive;

export default adminSlice.reducer; 